import React, { useEffect, useState, useRef } from 'react';
import { X, Check, ChevronDown, Zap, CheckCircle, AlertCircle, Sparkles, Building2, User, Star, TrendingUp, RotateCcw, HelpCircle } from 'lucide-react';
import { SelectedGLDimensions } from '../types/glCoding';
import { mockGLCodingData } from '../data/mockGLData';
import { Button } from './ui/Button';
import { DraggableModal } from './ui/DraggableModal';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
interface GLCodingModalProps {
  currentGLCode?: string;
  currentDimensions?: SelectedGLDimensions;
  lineItemDescription: string;
  onApply: (glCode: string, dimensions: SelectedGLDimensions) => void;
  onClose: () => void;
}
const dimensionOrder = ['Department', 'Cost Centre', 'Project', 'Activity', 'Region'];
// Mock vendor defaults - aligned with available dimensions
const mockVendorDefaults: SelectedGLDimensions = {
  Department: '200',
  'Cost Centre': '2010',
  Project: 'BAU',
  Activity: 'A800',
  Region: 'GLOBAL'
};
// Mock user role defaults - aligned with available dimensions
const mockUserDefaults: SelectedGLDimensions = {
  Department: '100',
  'Cost Centre': '1010',
  Project: 'P001',
  Activity: 'A200',
  Region: 'GLOBAL'
};
// Mock favorites - aligned with available dimensions
const mockFavorites = [{
  id: '1',
  name: 'IT Hardware Purchases',
  glCode: '400-4010-P006-A400-GLOBAL',
  dimensions: {
    Department: '400',
    'Cost Centre': '4010',
    Project: 'P006',
    Activity: 'A400',
    Region: 'GLOBAL'
  }
}, {
  id: '2',
  name: 'Office Supplies',
  glCode: '200-2020-BAU-A800-GLOBAL',
  dimensions: {
    Department: '200',
    'Cost Centre': '2020',
    Project: 'BAU',
    Activity: 'A800',
    Region: 'GLOBAL'
  }
}];
// Mock top used - aligned with available dimensions
const mockTopUsed = [{
  glCode: '100-1010-P001-A200-GLOBAL',
  useCount: 12,
  dimensions: {
    Department: '100',
    'Cost Centre': '1010',
    Project: 'P001',
    Activity: 'A200',
    Region: 'GLOBAL'
  }
}, {
  glCode: '400-4010-P006-A400-GLOBAL',
  useCount: 8,
  dimensions: {
    Department: '400',
    'Cost Centre': '4010',
    Project: 'P006',
    Activity: 'A400',
    Region: 'GLOBAL'
  }
}, {
  glCode: '300-3010-P005-A600-NA-US',
  useCount: 5,
  dimensions: {
    Department: '300',
    'Cost Centre': '3010',
    Project: 'P005',
    Activity: 'A600',
    Region: 'NA-US'
  }
}];
const mockVendor = {
  name: 'Abbott Australasia Pty Ltd',
  category: 'Healthcare & Medical Supplies'
};
const mockUser = {
  name: 'Sarah Chen',
  role: 'Finance Manager'
};
interface DimensionSelectorProps {
  dimensionName: string;
  selectedValue: string | undefined;
  onChange: (value: string) => void;
  disabled?: boolean;
  stepNumber: number;
  isComplete: boolean;
  isCurrent: boolean;
}
function DimensionSelector({
  dimensionName,
  selectedValue,
  onChange,
  disabled = false,
  stepNumber,
  isComplete,
  isCurrent
}: DimensionSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dimensions = mockGLCodingData.dimensions[dimensionName] || [];
  // Sync input value with selected value
  useEffect(() => {
    if (selectedValue) {
      const selected = dimensions.find(d => d.code === selectedValue);
      setInputValue(selected ? `${selected.code} - ${selected.description}` : selectedValue);
    } else {
      setInputValue('');
    }
  }, [selectedValue, dimensions]);
  // Filter options based on input
  const filteredOptions = dimensions.filter(option => {
    const searchText = inputValue.toLowerCase();
    return option.code.toLowerCase().includes(searchText) || option.description.toLowerCase().includes(searchText);
  });
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsOpen(true);
    setHighlightedIndex(0);
    const exactMatch = dimensions.find(d => d.code === newValue);
    if (exactMatch) {
      onChange(exactMatch.code);
    }
  };
  const handleSelectOption = (code: string) => {
    onChange(code);
    setIsOpen(false);
    inputRef.current?.blur();
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setIsOpen(true);
      return;
    }
    if (!isOpen) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => prev < filteredOptions.length - 1 ? prev + 1 : prev);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredOptions[highlightedIndex]) {
          handleSelectOption(filteredOptions[highlightedIndex].code);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        inputRef.current?.blur();
        break;
      case 'Tab':
        setIsOpen(false);
        break;
    }
  };
  // Scroll highlighted option into view
  useEffect(() => {
    if (isOpen && dropdownRef.current && filteredOptions.length > 0) {
      const highlightedElement = dropdownRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: 'nearest'
        });
      }
    }
  }, [highlightedIndex, isOpen, filteredOptions.length]);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  return <div ref={containerRef} className={`relative flex flex-col gap-1.5 transition-all duration-200 ${isCurrent ? 'ring-2 ring-blue-500 ring-opacity-50 rounded-lg p-3 -m-3 bg-blue-50' : ''}`} data-id="element-860">
      <div className="flex items-center gap-2" data-id="element-861">
        <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${isComplete ? 'bg-green-500 text-white' : isCurrent ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'}`} data-id="element-862">
          {isComplete ? <CheckCircle size={16} className="animate-scale-in" data-id="element-863" /> : stepNumber}
        </div>
        <label className="text-sm font-medium text-gray-900 flex items-center gap-1.5" data-id="element-864">
          {dimensionName}
          <span className="text-red-500" data-id="element-865">*</span>
        </label>
        {isComplete && <CheckCircle size={16} className="text-green-500 ml-auto animate-scale-in" data-id="element-866" />}
      </div>

      <div className="relative" data-id="element-867">
        <input ref={inputRef} type="text" value={inputValue} onChange={handleInputChange} onFocus={() => !disabled && setIsOpen(true)} onKeyDown={handleKeyDown} disabled={disabled} placeholder={disabled ? `Complete previous step first` : `Type or select ${dimensionName}...`} className={`w-full px-3 py-2 pr-10 border rounded bg-white text-sm
                     focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                     disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
                     transition-all duration-200 ${isCurrent ? 'border-blue-500 shadow-sm' : 'border-gray-300'}`} data-id="element-868" />
        <button type="button" onClick={() => !disabled && setIsOpen(!isOpen)} disabled={disabled} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed transition-colors" data-id="element-869">
          <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-870" />
        </button>

        {isOpen && !disabled && filteredOptions.length > 0 && <div ref={dropdownRef} className="absolute z-[100] left-0 right-0 top-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto animate-fade-in" data-id="element-871">
            {filteredOptions.map((option, index) => <button key={option.code} type="button" onClick={() => handleSelectOption(option.code)} onMouseEnter={() => setHighlightedIndex(index)} className={`w-full px-3 py-2 text-left transition-colors ${index === highlightedIndex ? 'bg-blue-50' : ''} ${option.code === selectedValue ? 'bg-blue-100' : 'hover:bg-blue-50'}`} data-id="element-872">
                <div className="flex items-center justify-between gap-3" data-id="element-873">
                  <div className="flex-1 min-w-0" data-id="element-874">
                    <div className="text-sm font-medium text-gray-900" data-id="element-875">
                      {option.code}
                    </div>
                    <div className="text-xs text-gray-600 truncate" data-id="element-876">
                      {option.description}
                    </div>
                  </div>
                  {option.code === selectedValue && <Check className="w-4 h-4 text-blue-600 flex-shrink-0" data-id="element-877" />}
                </div>
              </button>)}
          </div>}
      </div>
    </div>;
}
export function GLCodingModal({
  currentGLCode,
  currentDimensions,
  lineItemDescription,
  onApply,
  onClose
}: GLCodingModalProps) {
  const [selectedDimensions, setSelectedDimensions] = useState<SelectedGLDimensions>(currentDimensions || {});
  const [fullCodeInput, setFullCodeInput] = useState('');
  const [fullCodeError, setFullCodeError] = useState('');
  const [fullCodeSuccess, setFullCodeSuccess] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [saveFavoriteMode, setSaveFavoriteMode] = useState(false);
  const [favoriteName, setFavoriteName] = useState('');
  // Ref for the quick entry input field
  const quickEntryInputRef = useRef<HTMLInputElement>(null);
  // Keyboard shortcuts
  useKeyboardShortcuts({
    shortcuts: [{
      key: 'Escape',
      handler: onClose,
      description: 'Close modal'
    }],
    enabled: true
  });
  // Auto-focus the quick entry field when modal opens
  useEffect(() => {
    if (quickEntryInputRef.current) {
      quickEntryInputRef.current.focus();
    }
  }, []);
  // Helper function to filter dimensions to only those that exist in current configuration
  const filterToAvailableDimensions = (dimensions: SelectedGLDimensions): SelectedGLDimensions => {
    const filtered: SelectedGLDimensions = {};
    dimensionOrder.forEach(dimensionName => {
      if (dimensions[dimensionName]) {
        filtered[dimensionName] = dimensions[dimensionName];
      }
    });
    return filtered;
  };
  // Helper function to get dimension description
  const getDimensionDescription = (dimensionName: string, code: string): string => {
    const dimension = mockGLCodingData.dimensions[dimensionName]?.find(d => d.code === code);
    return dimension?.description || code;
  };
  const handleLoadSuggestion = (dimensions: SelectedGLDimensions) => {
    const filteredDimensions = filterToAvailableDimensions(dimensions);
    setSelectedDimensions(filteredDimensions);
    setFullCodeSuccess(false);
  };
  const handleDimensionChange = (dimensionName: string, value: string) => {
    const newSelected = {
      ...selectedDimensions
    };
    newSelected[dimensionName] = value;
    const currentIndex = dimensionOrder.indexOf(dimensionName);
    for (let i = currentIndex + 1; i < dimensionOrder.length; i++) {
      delete newSelected[dimensionOrder[i]];
    }
    setSelectedDimensions(newSelected);
    setFullCodeSuccess(false);
  };
  const parseFullCode = (code: string) => {
    if (!code.trim()) {
      setFullCodeError('');
      setFullCodeSuccess(false);
      return;
    }
    const parts = code.split(/[.\-]/);
    if (parts.length !== dimensionOrder.length) {
      setFullCodeError(`Expected ${dimensionOrder.length} dimensions, got ${parts.length}. Format: ${dimensionOrder.join(' - ')}`);
      setFullCodeSuccess(false);
      return;
    }
    const newSelected: SelectedGLDimensions = {};
    let allValid = true;
    parts.forEach((part, index) => {
      const dimensionName = dimensionOrder[index];
      const trimmedPart = part.trim();
      if (trimmedPart) {
        newSelected[dimensionName] = trimmedPart;
      } else {
        allValid = false;
      }
    });
    if (!allValid) {
      setFullCodeError('All dimension values must be provided');
      setFullCodeSuccess(false);
      return;
    }
    setSelectedDimensions(newSelected);
    setFullCodeError('');
    setFullCodeSuccess(true);
  };
  const handleFullCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFullCodeInput(value);
    setFullCodeSuccess(false);
    setFullCodeError('');
  };
  const handleFullCodeBlur = () => {
    if (fullCodeInput.trim()) {
      parseFullCode(fullCodeInput);
    }
  };
  const handleFullCodeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      parseFullCode(fullCodeInput);
    }
  };
  const assembleGLCode = (): string => {
    const codes = dimensionOrder.map(name => selectedDimensions[name]).filter(Boolean);
    return codes.join(mockGLCodingData.separator);
  };
  const isComplete = dimensionOrder.every(name => selectedDimensions[name]);
  const glCode = assembleGLCode();
  const completedCount = dimensionOrder.filter(name => selectedDimensions[name]).length;
  const currentStep = dimensionOrder.findIndex(name => !selectedDimensions[name]);
  const handleApply = () => {
    if (isComplete) {
      onApply(glCode, selectedDimensions);
    }
  };
  const handleClearAll = () => {
    setSelectedDimensions({});
    setFullCodeInput('');
    setFullCodeError('');
    setFullCodeSuccess(false);
  };
  return <DraggableModal isOpen={true} onClose={onClose} title="GL Coding" defaultWidth={1000} defaultHeight={800} minWidth={800} minHeight={600} data-id="element-878">
      <div className="flex flex-col h-full" data-id="element-879">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white flex-shrink-0" data-id="element-880">
          <div className="flex items-start justify-between" data-id="element-881">
            <div className="flex-1" data-id="element-882">
              <div className="flex items-center gap-3 flex-wrap" data-id="element-883">
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-teal-100 rounded-full" data-id="element-884">
                  <Building2 size={14} className="text-teal-700" data-id="element-885" />
                  <span className="text-xs font-medium text-teal-700" data-id="element-886">
                    {mockVendor.name}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-100 rounded-full" data-id="element-887">
                  <User size={14} className="text-blue-700" data-id="element-888" />
                  <span className="text-xs font-medium text-blue-700" data-id="element-889">
                    {mockUser.name} • {mockUser.role}
                  </span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-0.5" data-id="element-890">
                {lineItemDescription}
              </p>
              <p className="text-xs text-gray-500 mt-2" data-id="element-891">
                Select financial dimensions to generate the GL code for this
                invoice line
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors ml-4" aria-label="Close dialog" data-id="element-892">
              <X size={20} data-id="element-893" />
            </button>
          </div>

          {/* Progress Indicator */}
          <div className="mt-4" data-id="element-894">
            <div className="flex items-center justify-between text-sm mb-2" data-id="element-895">
              <span className="font-medium text-gray-700" data-id="element-896">
                Step {Math.min(completedCount + 1, dimensionOrder.length)} of{' '}
                {dimensionOrder.length}
              </span>
              <span className="text-gray-600" data-id="element-897">
                {completedCount} / {dimensionOrder.length} completed
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden" data-id="element-898">
              <div className="bg-blue-500 h-2 rounded-full transition-all duration-500 ease-out" style={{
              width: `${completedCount / dimensionOrder.length * 100}%`
            }} data-id="element-899" />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-900">
          <div className="space-y-6" data-id="element-901">
            {/* Full Code Entry Section */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-4 border-2 border-amber-200" data-id="element-902">
              <div className="flex items-center gap-2 mb-3" data-id="element-903">
                <Zap size={18} className="text-amber-600" data-id="element-904" />
                <h3 className="text-sm font-semibold text-gray-900" data-id="element-905">
                  Quick Entry
                </h3>
                <span className="text-xs text-gray-600 ml-auto" data-id="element-906">
                  For experienced users
                </span>
              </div>
              <div className="relative" data-id="element-907">
                <input ref={quickEntryInputRef} type="text" value={fullCodeInput} onChange={handleFullCodeChange} onBlur={handleFullCodeBlur} onKeyDown={handleFullCodeKeyDown} placeholder="Enter complete GL code (e.g., 100-1010-P001-A200-GLOBAL or 100.1010.P001.A200.GLOBAL)" className="w-full px-4 py-2.5 pr-10 border-2 border-amber-300 rounded-lg bg-white text-sm font-mono
                           focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent
                           placeholder:text-gray-400 placeholder:font-sans" data-id="element-908" />
                {fullCodeSuccess && <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" data-id="element-909" />}
                {fullCodeError && <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-500" data-id="element-910" />}
              </div>
              <div className="mt-2 flex items-start gap-2" data-id="element-911">
                {fullCodeSuccess ? <p className="text-xs text-green-700 flex items-center gap-1" data-id="element-912">
                    <CheckCircle size={14} data-id="element-913" />
                    GL code parsed successfully! All dimensions populated.
                  </p> : fullCodeError ? <p className="text-xs text-red-700 flex items-start gap-1" data-id="element-914">
                    <AlertCircle size={14} className="flex-shrink-0 mt-0.5" data-id="element-915" />
                    <span data-id="element-916">{fullCodeError}</span>
                  </p> : <p className="text-xs text-gray-600" data-id="element-917">
                    Use "-" or "." as separators. Press Enter or click away to
                    parse.
                  </p>}
              </div>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3" data-id="element-918">
              <div className="flex-1 border-t border-gray-300" data-id="element-919"></div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-920">
                Or select step by step
              </span>
              <div className="flex-1 border-t border-gray-300" data-id="element-921"></div>
            </div>

            {/* Smart Suggestions Section */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-4 border-2 border-purple-200" data-id="element-922">
              <button onClick={() => setShowSuggestions(!showSuggestions)} className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:text-gray-700 transition-colors w-full" data-id="element-923">
                <Sparkles size={18} className="text-purple-600" data-id="element-924" />
                Smart Suggestions
                <span className="text-xs text-gray-600 ml-auto mr-2" data-id="element-925">
                  Personalized for you
                </span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showSuggestions ? 'rotate-180' : ''}`} data-id="element-926" />
              </button>

              {showSuggestions && <div className="mt-4 space-y-4" data-id="element-927">
                  {/* Vendor Default */}
                  <div data-id="element-928">
                    <div className="flex items-center gap-1.5 mb-2" data-id="element-929">
                      <Building2 size={14} className="text-teal-600" data-id="element-930" />
                      <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-931">
                        Vendor Default Configuration
                      </h4>
                    </div>
                    <button onClick={() => handleLoadSuggestion(mockVendorDefaults)} className="w-full text-left px-4 py-3 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg border-2 border-teal-300 hover:border-teal-500 hover:shadow-md transition-all group" data-id="element-932">
                      <div className="flex items-start justify-between gap-3" data-id="element-933">
                        <div className="flex-1" data-id="element-934">
                          <div className="flex items-center gap-2 mb-1" data-id="element-935">
                            <div className="text-sm font-bold text-teal-900 group-hover:text-teal-700" data-id="element-936">
                              {mockVendor.name}
                            </div>
                            <span className="px-2 py-0.5 bg-teal-200 text-teal-800 text-xs font-medium rounded" data-id="element-937">
                              {mockVendor.category}
                            </span>
                          </div>
                          <div className="text-xs text-teal-700 mb-2" data-id="element-938">
                            Standard healthcare and medical supply purchases
                          </div>
                          <div className="flex flex-wrap gap-1.5" data-id="element-939">
                            {dimensionOrder.map(dimensionName => {
                          const code = mockVendorDefaults[dimensionName];
                          if (!code) return null;
                          return <span key={dimensionName} className="px-2 py-0.5 bg-white text-teal-900 text-xs rounded border border-teal-200" data-id="element-940">
                                  {dimensionName}: {code}
                                </span>;
                        })}
                          </div>
                        </div>
                        <Sparkles size={18} className="text-teal-500 group-hover:text-teal-600 flex-shrink-0" data-id="element-941" />
                      </div>
                    </button>
                  </div>

                  {/* User Role Defaults */}
                  <div data-id="element-942">
                    <div className="flex items-center gap-1.5 mb-2" data-id="element-943">
                      <User size={14} className="text-indigo-600" data-id="element-944" />
                      <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-945">
                        Your Role Defaults
                      </h4>
                    </div>
                    <button onClick={() => handleLoadSuggestion(mockUserDefaults)} className="w-full text-left px-3 py-2.5 bg-white rounded-lg border border-indigo-200 hover:border-indigo-400 hover:bg-indigo-50 transition-all group" data-id="element-946">
                      <div className="flex items-center justify-between" data-id="element-947">
                        <div data-id="element-948">
                          <div className="text-sm font-medium text-gray-900 group-hover:text-indigo-700" data-id="element-949">
                            {mockUser.role} Preset
                          </div>
                          <div className="text-xs text-gray-600 mt-0.5" data-id="element-950">
                            {dimensionOrder.filter(name => mockUserDefaults[name]).map((name, index, arr) => <span key={name} data-id="element-951">
                                  {name}: {mockUserDefaults[name]}
                                  {index < arr.length - 1 ? ' • ' : ''}
                                </span>)}
                          </div>
                        </div>
                        <Zap size={16} className="text-indigo-400 group-hover:text-indigo-600" data-id="element-952" />
                      </div>
                    </button>
                  </div>

                  {/* Favorites */}
                  <div data-id="element-953">
                    <div className="flex items-center gap-1.5 mb-2" data-id="element-954">
                      <Star size={14} className="text-amber-600 fill-amber-600" data-id="element-955" />
                      <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-956">
                        My Favorites
                      </h4>
                    </div>
                    <div className="space-y-2" data-id="element-957">
                      {mockFavorites.map(fav => <button key={fav.id} onClick={() => handleLoadSuggestion(fav.dimensions)} className="w-full text-left px-3 py-2 bg-white rounded-lg border border-amber-200 hover:border-amber-400 hover:bg-amber-50 transition-all group" data-id="element-958">
                          <div className="flex items-center justify-between" data-id="element-959">
                            <div className="flex-1 min-w-0" data-id="element-960">
                              <div className="text-sm font-medium text-gray-900 group-hover:text-amber-700" data-id="element-961">
                                {fav.name}
                              </div>
                              <code className="text-xs font-mono text-gray-600" data-id="element-962">
                                {fav.glCode}
                              </code>
                            </div>
                          </div>
                        </button>)}
                    </div>
                  </div>

                  {/* Top Used */}
                  <div data-id="element-963">
                    <div className="flex items-center gap-1.5 mb-2" data-id="element-964">
                      <TrendingUp size={14} className="text-green-600" data-id="element-965" />
                      <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-966">
                        My Top Used
                      </h4>
                    </div>
                    <div className="space-y-2" data-id="element-967">
                      {mockTopUsed.map((item, index) => <button key={item.glCode} onClick={() => handleLoadSuggestion(item.dimensions)} className="w-full text-left px-3 py-2 bg-white rounded-lg border border-green-200 hover:border-green-400 hover:bg-green-50 transition-all group" data-id="element-968">
                          <div className="flex items-center justify-between" data-id="element-969">
                            <div className="flex items-center gap-2 flex-1 min-w-0" data-id="element-970">
                              <span className="text-xs font-bold text-green-600 w-5" data-id="element-971">
                                #{index + 1}
                              </span>
                              <code className="text-xs font-mono text-gray-900 group-hover:text-green-700" data-id="element-972">
                                {item.glCode}
                              </code>
                            </div>
                            <span className="text-xs text-gray-500" data-id="element-973">
                              Used {item.useCount}x
                            </span>
                          </div>
                        </button>)}
                    </div>
                  </div>
                </div>}
            </div>

            {/* Dynamic dimension selectors */}
            <div className="space-y-4" data-id="element-974">
              {dimensionOrder.map((dimensionName, index) => {
              const previousDimensionName = index > 0 ? dimensionOrder[index - 1] : null;
              const isPreviousSelected = !previousDimensionName || selectedDimensions[previousDimensionName];
              const isComplete = !!selectedDimensions[dimensionName];
              const isCurrent = index === currentStep;
              return <DimensionSelector key={dimensionName} dimensionName={dimensionName} selectedValue={selectedDimensions[dimensionName]} onChange={value => handleDimensionChange(dimensionName, value)} disabled={!isPreviousSelected} stepNumber={index + 1} isComplete={isComplete} isCurrent={isCurrent} data-id="element-975" />;
            })}
            </div>

            {/* GL Code Summary Panel */}
            <div className="mt-6 pt-6 border-t-2 border-gray-200" data-id="element-976">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200" data-id="element-977">
                <div className="flex items-center justify-between mb-3" data-id="element-978">
                  <label className="text-sm font-semibold text-gray-900" data-id="element-979">
                    GL Code Summary
                  </label>
                  {isComplete && <button onClick={() => setSaveFavoriteMode(!saveFavoriteMode)} className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 rounded transition-colors" data-id="element-980">
                      <Star size={14} data-id="element-981" />
                      Save as Favorite
                    </button>}
                </div>

                {saveFavoriteMode && <div className="mb-3 flex gap-2" data-id="element-982">
                    <input type="text" value={favoriteName} onChange={e => setFavoriteName(e.target.value)} placeholder="Enter favorite name..." className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-amber-500" onKeyDown={e => {
                  if (e.key === 'Enter') {
                    setSaveFavoriteMode(false);
                    setFavoriteName('');
                  }
                }} data-id="element-983" />
                    <button onClick={() => {
                  setSaveFavoriteMode(false);
                  setFavoriteName('');
                }} disabled={!favoriteName.trim()} className="px-3 py-1.5 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors" data-id="element-984">
                      Save
                    </button>
                    <button onClick={() => {
                  setSaveFavoriteMode(false);
                  setFavoriteName('');
                }} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors" data-id="element-985">
                      Cancel
                    </button>
                  </div>}

                {completedCount > 0 ? <div className="space-y-3" data-id="element-986">
                    {/* Dimension breakdown */}
                    <div className="space-y-1.5" data-id="element-987">
                      {dimensionOrder.map(name => {
                    const value = selectedDimensions[name];
                    if (!value) return null;
                    const description = getDimensionDescription(name, value);
                    return <div key={name} className="flex items-center justify-between text-sm bg-white rounded px-3 py-2" data-id="element-988">
                            <span className="text-gray-600 font-medium" data-id="element-989">
                              {name}:
                            </span>
                            <span className="text-gray-900 font-mono" data-id="element-990">
                              {value} - {description}
                            </span>
                          </div>;
                  })}
                    </div>

                    {/* Final GL Code */}
                    <div className="bg-white rounded-lg px-4 py-3 border-2 border-blue-300" data-id="element-991">
                      <div className="text-xs font-medium text-gray-600 mb-1" data-id="element-992">
                        Complete GL Code:
                      </div>
                      <code className="text-lg font-mono font-bold text-blue-600" data-id="element-993">
                        {glCode}
                      </code>
                    </div>
                  </div> : <p className="text-sm text-gray-600 italic" data-id="element-994">
                    Select dimensions above to generate your GL code
                  </p>}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0" data-id="element-995">
          <button onClick={handleClearAll} disabled={completedCount === 0 && !fullCodeInput} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded
                     hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-996">
            <RotateCcw size={16} data-id="element-997" />
            Clear All
          </button>
          <div className="flex items-center gap-3" data-id="element-998">
            <button onClick={handleApply} disabled={!isComplete} className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded
                       hover:bg-blue-700 transition-colors
                       disabled:bg-gray-300 disabled:cursor-not-allowed disabled:hover:bg-gray-300
                       shadow-sm hover:shadow" data-id="element-999">
              Apply GL Code
            </button>
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded
                       hover:bg-gray-50 transition-colors" data-id="element-1000">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </DraggableModal>;
}